import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

declare global {
  interface Window {
    grecaptcha?: any;
    reforalGuestCaptchaLoaded?: () => void;
  }
}

const SITE_KEY = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY || '';
// 'v2' shows the "I'm not a robot" checkbox; 'v3' is invisible and scored.
const VERSION = process.env.REACT_APP_GOOGLE_RECAPTCHA_VERSION === 'v3' ? 'v3' : 'v2';
const SCRIPT_ID = 'reforal-guest-recaptcha';

const loadScript = (onLoad: () => void) => {
  if (window.grecaptcha?.render) {
    onLoad();
    return;
  }
  window.reforalGuestCaptchaLoaded = onLoad;
  if (document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = VERSION === 'v3'
    ? `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}&onload=reforalGuestCaptchaLoaded`
    : 'https://www.google.com/recaptcha/api.js?render=explicit&onload=reforalGuestCaptchaLoaded';
  document.head.appendChild(script);
};

/**
 * ENH-052 (plan D8): reCAPTCHA for the guest form, only when the server asks
 * for it (`captcha_required`). With no site key nothing is shown and no token
 * is sent; the server then refuses the submission (it fails closed), which the
 * form reports like any captcha failure.
 */
const useGuestCaptcha = (required: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);
  const [ token, setToken ] = useState<string | null>(null);
  const [ loaded, setLoaded ] = useState(false);
  const active = required && !!SITE_KEY;

  useEffect(() => {
    if (!active) return;
    loadScript(() => setLoaded(true));
  }, [ active ]);

  useEffect(() => {
    if (!active || !loaded || VERSION !== 'v2' || !containerRef.current || widgetId.current !== null) return;
    window.grecaptcha.ready(() => {
      if (!containerRef.current || widgetId.current !== null) return;
      widgetId.current = window.grecaptcha.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (value: string) => setToken(value),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
      });
    });
  });

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!active) return null;
    if (VERSION === 'v2') return token;
    if (!loaded) return null;
    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(SITE_KEY, { action: 'guest_referral' })
          .then((value: string) => resolve(value))
          .catch(() => resolve(null));
      });
    });
  }, [ active, loaded, token ]);

  const reset = useCallback(() => {
    if (VERSION === 'v2' && widgetId.current !== null) {
      window.grecaptcha?.reset(widgetId.current);
    }
    setToken(null);
  }, []);

  return {
    // v3 runs invisibly; only the v2 checkbox needs room on the page.
    widget: active && VERSION === 'v2' ? <div ref={containerRef} className="guest-refer-captcha" /> : null,
    // Without a site key the server decides; the guest is not blocked here.
    ready: !active || VERSION === 'v3' || !!token,
    getToken,
    reset,
  };
};

export default useGuestCaptcha;
